<template>
  <div
    class="similar-properties-wrapper"
    v-if="!hasFetched || loading || hotels.length > 0"
  >
    <div class="similar-properties-header">
      <h2 class="text-title-2 fw-semi-bold">You May Also Like</h2>
      <div class="similar-properties-nav" v-if="!loading && hotels.length > 4">
        <button
          class="similar-properties-nav__btn"
          @click="scroll('left')"
          :disabled="isAtStart"
          aria-label="Previous"
        >
          <span class="icon icon-chevron-left"></span>
        </button>
        <button
          class="similar-properties-nav__btn"
          @click="scroll('right')"
          :disabled="isAtEnd"
          aria-label="Next"
        >
          <span class="icon icon-chevron-right"></span>
        </button>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div class="similar-properties-carousel" v-if="loading" ref="scrollContainer">
      <div class="similar-properties-track">
        <HotelCardCompactSkeleton
          v-for="i in 4"
          :key="i"
          class="similar-properties-card"
        />
      </div>
    </div>

    <!-- Scrollable hotel cards -->
    <div class="similar-properties-carousel" v-else ref="scrollContainer" @scroll="onScroll">
      <div class="similar-properties-track">
        <HotelCardCompact
          v-for="hotel in hotels"
          :key="hotel.id"
          :hotel="hotel"
          :route="props.route"
          :cheapestRate="hotel.cheapest_rate"
          :priceFrom="hotel.price_from"
          class="similar-properties-card"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import HotelCardCompact from '~/components/MarketPage/HotelCardCompact.vue'
import HotelCardCompactSkeleton from '~/components/MarketPage/HotelCardCompactSkeleton.vue'

const api = useApi()

const props = defineProps({
  currentHotel: { type: Object, required: true },
  route: { type: Object },
  currentPrice: { type: Number, default: undefined }
})

const loading = ref(true)
const hotels = ref<any[]>([])
const scrollContainer = ref<HTMLElement | null>(null)
const hasFetched = ref(false)

// Brand-scoped minimum hotel star rating (e.g. Solaire = 4★+); 0 = no floor.
const brandMinHotelStars = (useBrand().feature('minHotelStars') as number) || 0

const isAtStart = ref(true)
const isAtEnd = ref(false)

let scrollTimeout: ReturnType<typeof setTimeout> | null = null
const onScroll = () => {
  if (scrollTimeout) return
  scrollTimeout = setTimeout(() => {
    scrollTimeout = null
    updateScrollPosition()
  }, 50)
}

const updateScrollPosition = () => {
  const container = scrollContainer.value
  if (!container) return
  isAtStart.value = container.scrollLeft <= 10
  isAtEnd.value = container.scrollLeft >= container.scrollWidth - container.clientWidth - 10
}

const scroll = (direction: string) => {
  const container = scrollContainer.value
  if (!container) return
  const scrollAmount = 300
  container.scrollBy({
    left: direction === 'left' ? -scrollAmount : scrollAmount,
    behavior: 'smooth',
  })
}

async function fetchSimilarHotels() {
  if (hasFetched.value) return

  const geoPlaceId = props.currentHotel?.geo_place?.id
  if (!geoPlaceId) {
    loading.value = false
    return
  }

  hasFetched.value = true
  loading.value = true
  const currentRating = props.currentHotel?.rating || 3
  const filters: Record<string, any> = {
    geo_place_id: geoPlaceId,
    rating_gte: Math.max(brandMinHotelStars, 1, currentRating - 1),
    rating_lte: Math.min(5, currentRating + 1),
  }

  try {
    const dateStart = props.route?.query?.['date_start'] || props.route?.query?.['checkin-date']
    const dateEnd = props.route?.query?.['date_end'] || props.route?.query?.['checkout-date']
    const adults = props.route?.query?.adults || 2

    let response: any
    if (dateStart && dateEnd) {
      // Availability search with pricing
      response = await api('/api/product/v3/hotels/search-availability', {
        method: 'POST',
        body: {
          criteria: { date_start: dateStart, date_end: dateEnd, adults: Number(adults) },
          filters,
          sort: 'rating_desc'
        }
      })
    } else {
      // Static search without pricing - different API format
      response = await api('/api/product/v3/hotels/search', {
        method: 'POST',
        body: {
          geo_place_id: geoPlaceId,
          rating_gte: Math.max(brandMinHotelStars, 1, currentRating - 1),
          sort_by: 'rating_desc'
        }
      })
    }

    const rawList = response?.suggestions || response?.hotels || []
    let hotelList = rawList.map((item: any) => {
      if (item.hotel) {
        return { ...item.hotel, price_from: item.price_from, cheapest_rate: item.cheapest_rate }
      }
      return item
    })

    let filteredHotels = hotelList.filter((h: any) => h.id !== props.currentHotel.id)

    if (props.currentPrice && filteredHotels.some((h: any) => h.price_from?.converted?.amount)) {
      const minPrice = props.currentPrice * 0.5
      const maxPrice = props.currentPrice * 1.5
      const inRange = filteredHotels.filter((h: any) => {
        const price = Number(h.price_from?.converted?.amount)
        return price && price >= minPrice && price <= maxPrice
      })
      if (inRange.length >= 4) filteredHotels = inRange
    }

    hotels.value = filteredHotels.slice(0, 8)
    nextTick(updateScrollPosition)
  } catch (e) {
    hotels.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchSimilarHotels()
})
</script>

<style lang="scss" scoped>
.similar-properties-wrapper {
  padding-bottom: 32px;
  padding-top: 8px; // Give space so card shadows/borders aren't cut off
  width: 100%;

  @media screen and (max-width: 430px) {
    padding-bottom: 80px;
  }
}

.similar-properties-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h2 {
    margin: 0;
  }
}

.similar-properties-nav {
  display: flex;
  gap: 8px;

  @media screen and (max-width: 768px) {
    display: none;
  }

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    width: 32px;
    height: 32px;
    background: var(--blue-highlight);
    color: var(--blue);
    border: none;
    border-radius: 10px;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: var(--blue);
      color: #fff;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}

.similar-properties-carousel {
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-top: 4px; // Prevent card top from being cut off
  margin-top: -4px;

  &::-webkit-scrollbar {
    display: none;
  }
}

.similar-properties-track {
  display: inline-flex;
  gap: 24px;
  padding-bottom: 8px;

  @media screen and (max-width: 430px) {
    gap: 16px;
  }
}

.similar-properties-card {
  flex: 0 0 280px;
  width: 280px;

  @media screen and (max-width: 430px) {
    flex: 0 0 260px;
    width: 260px;
  }
}
</style>
